import React, { useState, useEffect } from 'react'
import Sidebar from './components/Sidebar'
import { Mail, ChevronDown } from 'lucide-react'

// Shared dark-theme admin UI styles (site palette: #252525 / #b7a78b)
const ui = {
  card: 'bg-[#1d1d1d] rounded-xl border border-[#484540] p-6 md:p-8 shadow-lg',
  title: 'text-xl font-semibold mb-1 capitalize text-[#e7ddd3] tracking-wide flex items-center gap-2',
  subtitle: 'text-sm text-[#8a8175] mb-6',
  tableWrap: 'overflow-x-auto rounded-lg border border-[#484540]',
  table: 'min-w-full divide-y divide-[#484540]',
  th: 'px-6 py-3 text-left text-xs font-semibold text-[#8a8175] uppercase tracking-wider bg-[#252525]',
  td: 'px-6 py-4 align-top text-sm text-[#cdc2ad]',
  tr: 'hover:bg-[#252525] transition-colors',
}

// "12 Jul 2026, 14:03"
const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const Contact = () => {
  const [tableData, setTableData] = useState([])
  // Messages can run long, so each row starts collapsed to two lines.
  const [expanded, setExpanded] = useState([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`)
      if (response.ok) {
        setTableData(await response.json())
      } else {
        console.error('Error fetching data')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const toggleMessage = (id) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  return (
    <div className="flex min-h-screen bg-[#252525]" style={{ fontFamily: 'Muli' }}>
      <Sidebar />

      <div className="flex-1 bg-[#252525] min-h-screen pb-16">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#484540] bg-[#1d1d1d] px-6 py-4">
          <p className="text-sm font-medium text-[#8a8175]">Admin Dashboard</p>
          <span className="text-sm font-semibold text-[#e7ddd3]">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-[#e7ddd3]">Contact form</h1>
            <p className="mt-1 text-sm text-[#8a8175]">Messages sent through the contact page.</p>
          </div>

          <div className={ui.card}>
            <h2 className={ui.title}>
              <Mail className="h-5 w-5 text-[#b7a78b]" /> Enquiries
              <span className="ml-1 text-sm font-normal text-[#8a8175]">({tableData.length})</span>
            </h2>
            <p className={ui.subtitle}>Newest messages first — click a message to read it in full.</p>

            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th className={ui.th}>s/n</th>
                    <th className={ui.th}>name</th>
                    <th className={ui.th}>email</th>
                    <th className={ui.th}>message</th>
                    <th className={ui.th}>date received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#484540]">
                  {tableData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-sm text-[#6f675c]">
                        No messages yet.
                      </td>
                    </tr>
                  ) : (
                    tableData.map((contact, index) => {
                      const isOpen = expanded.includes(contact._id)
                      return (
                        <tr className={ui.tr} key={contact._id}>
                          <td className={ui.td}>{index + 1}</td>
                          <td className={`${ui.td} capitalize whitespace-nowrap text-[#e7ddd3]`}>
                            {contact.fullName}
                          </td>
                          <td className={`${ui.td} whitespace-nowrap`}>
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-[#efefcb] hover:underline"
                            >
                              {contact.email}
                            </a>
                          </td>
                          <td className={`${ui.td} min-w-[280px] max-w-md`}>
                            <button
                              type="button"
                              onClick={() => toggleMessage(contact._id)}
                              className="group flex w-full items-start gap-2 text-left"
                            >
                              <span
                                className={`whitespace-pre-line ${isOpen ? '' : 'line-clamp-2'}`}
                              >
                                {contact.message}
                              </span>
                              <ChevronDown
                                className={`mt-0.5 h-4 w-4 shrink-0 text-[#8a8175] transition-transform group-hover:text-[#b7a78b] ${
                                  isOpen ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                          </td>
                          <td className={`${ui.td} whitespace-nowrap text-[#8a8175]`}>
                            {formatDate(contact.receivedAt)}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
